/**
 * NDA Acceptance Component - CR-1403
 *
 * Component for suppliers to review and accept/decline NDA:
 * - Display NDA document preview/download
 * - Accept/Decline buttons with confirmation
 * - Shows NDA status and history
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  Loader2,
  FileText,
  Download,
  CheckCircle,
  XCircle,
  Shield,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { rfqAPI } from '../../lib/api';
import type { RFQNDA, RFQDocument } from '../../lib/api';

interface NDAAcceptanceProps {
  nda?: RFQNDA;
  document?: RFQDocument;
  onAccept: () => void | Promise<void>;
  onDecline: () => void | Promise<void>;
  loading?: boolean;
}

export default function NDAAcceptance({ nda, document, onAccept, onDecline, loading: externalLoading }: NDAAcceptanceProps) {
  const [internalLoading, setInternalLoading] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  const loading = externalLoading || internalLoading;

  const getStatusBadge = () => {
    if (!nda) {
      return (
        <Badge variant="outline" className="border-yellow-500 text-yellow-600">
          <Clock className="h-3 w-3 mr-1" />
          Pending Review
        </Badge>
      );
    }
    switch (nda.status) {
      case 'pending':
        return (
          <Badge variant="outline" className="border-yellow-500 text-yellow-600">
            <Clock className="h-3 w-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'accepted':
        return (
          <Badge className="bg-green-500">
            <CheckCircle className="h-3 w-3 mr-1" />
            Accepted
          </Badge>
        );
      case 'declined':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Declined
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleAccept = async () => {
    if (!acceptTerms || !hasReviewed) {
      toast.error('Please review the NDA and accept the terms');
      return;
    }

    try {
      setInternalLoading(true);
      if (nda) {
        await rfqAPI.acceptNDA(nda.id);
        toast.success('NDA accepted successfully');
      }
      setShowAcceptDialog(false);
      await onAccept();
    } catch (error) {
      console.error('Error accepting NDA:', error);
      toast.error('Failed to accept NDA');
    } finally {
      setInternalLoading(false);
    }
  };

  const handleDecline = async () => {
    try {
      setInternalLoading(true);
      if (nda) {
        await rfqAPI.declineNDA(nda.id);
        toast.success('NDA declined');
      }
      setShowDeclineDialog(false);
      await onDecline();
    } catch (error) {
      console.error('Error declining NDA:', error);
      toast.error('Failed to decline NDA');
    } finally {
      setInternalLoading(false);
    }
  };

  const handleDownload = () => {
    if (document?.file) {
      window.open(document.file, '_blank');
      setHasReviewed(true);
    } else {
      toast.error('NDA document not available');
    }
  };

  // Already responded
  if (nda && nda.status !== 'pending') {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Non-Disclosure Agreement</CardTitle>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 rounded-lg p-4">
            {nda.status === 'accepted' ? (
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <div>
                  <p className="font-medium">NDA Accepted</p>
                  <p className="text-sm text-muted-foreground">
                    You accepted this NDA on{' '}
                    {nda.responded_at
                      ? new Date(nda.responded_at).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <XCircle className="h-8 w-8 text-red-500" />
                <div>
                  <p className="font-medium">NDA Declined</p>
                  <p className="text-sm text-muted-foreground">
                    You declined this NDA. You cannot submit a response to this RFQ.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-yellow-200 bg-yellow-50/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-yellow-600" />
              <CardTitle>Non-Disclosure Agreement Required</CardTitle>
            </div>
            {getStatusBadge()}
          </div>
          <CardDescription>
            You must accept the NDA before viewing or responding to this RFQ
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* NDA Document */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-muted-foreground" />
                <div>
                  <p className="font-medium">
                    {document?.file_name || 'Non-Disclosure Agreement'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {document?.file_type?.toUpperCase() || 'PDF'} Document
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download & Review
              </Button>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-yellow-100 border border-yellow-200 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-yellow-800">Important Notice</p>
              <p className="text-yellow-700 mt-1">
                Please carefully review the Non-Disclosure Agreement before proceeding.
                By accepting, you agree to be bound by its terms. If you decline, you
                will not be able to submit a response to this RFQ.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowDeclineDialog(true)}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Decline NDA
            </Button>
            <Button
              onClick={() => setShowAcceptDialog(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Accept NDA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Accept Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept Non-Disclosure Agreement</DialogTitle>
            <DialogDescription>
              Please confirm you have reviewed and agree to the NDA terms.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="reviewed"
                checked={hasReviewed}
                onCheckedChange={(checked) => setHasReviewed(checked as boolean)}
              />
              <Label htmlFor="reviewed" className="text-sm leading-relaxed">
                I confirm that I have downloaded and reviewed the Non-Disclosure
                Agreement document
              </Label>
            </div>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="accept"
                checked={acceptTerms}
                onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
              />
              <Label htmlFor="accept" className="text-sm leading-relaxed">
                I accept the terms and conditions of the Non-Disclosure Agreement
                and understand that I am legally bound by them
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAcceptDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAccept}
              disabled={loading || !acceptTerms || !hasReviewed}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Accept NDA
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Non-Disclosure Agreement</DialogTitle>
            <DialogDescription>
              Are you sure you want to decline this NDA?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-800">Warning</p>
                <p className="text-red-700 mt-1">
                  If you decline this NDA, you will not be able to:
                </p>
                <ul className="list-disc list-inside text-red-700 mt-2 space-y-1">
                  <li>View the full RFQ details</li>
                  <li>Submit a response to this RFQ</li>
                  <li>Participate in this procurement process</li>
                </ul>
                <p className="text-red-700 mt-2">This action cannot be undone.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeclineDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDecline}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Declining...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Decline NDA
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
